import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import {
  Activity,
  ChevronRight,
  Clock3,
  Dumbbell,
  Flame,
  MapPin,
  PersonStanding,
  ShieldCheck,
  Waves,
} from "lucide-react";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { isAdminRole } from "../../../context/authUser";
import LogoutButton from "../../../components/ui/LogoutButton";
import UserTopNav from "../../../components/layout/UserTopNav";
import NoticeStack, { type NoticeItem, type NoticeTone } from "../../../components/ui/NoticeStack";
import {
  bookClass as bookClassRequest,
  cancelBooking as cancelBookingRequest,
  leaveWaitlist as leaveWaitlistRequest,
} from "../services/bookings";

type ClassDoc = {
  title: string;
  timezone?: string;
  startTime: Timestamp;
  endTime: Timestamp;
  coachName?: string;
  location?: string;
  capacity?: number;
  bookedCount?: number;
  waitlistCount?: number;
  status?: "scheduled" | "cancelled";
};

type BookingDoc = {
  classId: string;
  userId: string;
  userName?: string;
  status: "booked" | "cancelled";
  createdAt?: Timestamp;
  cancelledAt?: Timestamp;
};

type WaitlistDoc = {
  classId: string;
  userId: string;
  userName?: string;
  status: "waiting" | "promoted" | "left" | "expired";
  createdAt?: Timestamp;
  promotedAt?: Timestamp;
};

const DEFAULT_TZ = "Europe/London";

function computeBookingClosesAt(startTs?: Timestamp) {
  const start = startTs?.toDate?.();
  if (!start) return null;

  const closes = new Date(start);
  const hour = start.getHours();

  if (hour === 5 || hour === 6) {
    closes.setDate(closes.getDate() - 1);
    closes.setHours(20, 30, 0, 0);
    return closes;
  }

  if (hour === 18) {
    closes.setHours(15, 0, 0, 0);
    return closes;
  }

  closes.setTime(start.getTime() - 2 * 60 * 60 * 1000);
  return closes;
}

function bookingStatus(startTs?: Timestamp) {
  const start = startTs?.toDate?.();
  if (!start) return { state: "unknown" as const };

  const closes = computeBookingClosesAt(startTs);
  const now = Date.now();

  if (now >= start.getTime()) return { state: "started" as const, closes };
  if (closes && now >= closes.getTime()) return { state: "closed" as const, closes };

  return {
    state: "open" as const,
    closes,
    msLeft: closes ? closes.getTime() - now : 0,
  };
}

function cancelStatus(startTs?: Timestamp) {
  return bookingStatus(startTs);
}

function fmtRemaining(ms: number) {
  const mins = Math.ceil(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function fmtDate(d: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone,
  }).format(d);
}

function fmtTime(d: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(d);
}

function fmtDayLabel(d: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone,
  }).format(d);
}

function startOfWeekMonday(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diffToMonday = (day + 6) % 7;
  x.setDate(x.getDate() - diffToMonday);
  return x;
}

function scheduleWindowWithSaturdayCutover(now = new Date()) {
  const thisMon = startOfWeekMonday(now);
  const cutover = new Date(thisMon);
  cutover.setDate(cutover.getDate() + 5);
  cutover.setHours(10, 0, 0, 0);

  const showNextWeek = now.getTime() >= cutover.getTime();
  const from = new Date(thisMon);
  if (showNextWeek) from.setDate(from.getDate() + 7);

  const to = new Date(from);
  to.setDate(to.getDate() + 7);

  return { from, to, showNextWeek };
}

function classMeta(title?: string) {
  const t = (title || "").toLowerCase();

  if (t.includes("hyrox")) {
    return {
      label: "HYROX",
      stripe: "bg-[linear-gradient(180deg,rgba(96,165,250,0.95),rgba(59,84,170,0.9))]",
      pill: "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]",
      iconWrap: "border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.16)] text-[color:#dbe6ff]",
      Icon: Flame,
    };
  }

  if (t.includes("strength")) {
    return {
      label: "STRENGTH",
      stripe: "bg-[linear-gradient(180deg,rgba(59,130,246,0.95),rgba(14,116,144,0.9))]",
      pill: "border-sky-500/24 bg-sky-500/12 text-sky-200",
      iconWrap: "border-sky-500/20 bg-sky-500/12 text-sky-200",
      Icon: Dumbbell,
    };
  }

  if (t.includes("yoga")) {
    return {
      label: "YOGA",
      stripe: "bg-[linear-gradient(180deg,rgba(45,212,191,0.95),rgba(13,148,136,0.9))]",
      pill: "border-teal-500/24 bg-teal-500/12 text-teal-200",
      iconWrap: "border-teal-500/20 bg-teal-500/12 text-teal-200",
      Icon: PersonStanding,
    };
  }

  if (t.includes("run club")) {
    return {
      label: "RUN CLUB",
      stripe: "bg-[linear-gradient(180deg,rgba(125,211,252,0.95),rgba(59,130,246,0.9))]",
      pill: "border-cyan-400/24 bg-cyan-400/12 text-cyan-200",
      iconWrap: "border-cyan-400/20 bg-cyan-400/12 text-cyan-200",
      Icon: Waves,
    };
  }

  return {
    label: (title || "SESSION").toUpperCase(),
    stripe: "bg-[linear-gradient(180deg,rgba(148,163,184,0.85),rgba(71,85,105,0.8))]",
    pill: "border-white/12 bg-white/[0.05] text-white/80",
    iconWrap: "border-white/12 bg-white/[0.05] text-white/80",
    Icon: Activity,
  };
}

function capacityPercent(bookedCount: number, capacity: number) {
  if (!capacity || capacity <= 0) return 0;
  return Math.max(0, Math.min(100, (bookedCount / capacity) * 100));
}

export default function Schedule() {
  const navigate = useNavigate();
  const auth = getAuth();
  const { appUser } = useAuth();
  const isAdmin = isAdminRole(appUser?.role);

  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [classes, setClasses] = useState<Array<{ id: string; data: ClassDoc }>>([]);
  const [activeBookingsByClassId, setActiveBookingsByClassId] = useState<Record<string, BookingDoc>>({});
  const [activeWaitlistByClassId, setActiveWaitlistByClassId] = useState<Record<string, WaitlistDoc>>({});
  const [promotedWaitlistByClassId, setPromotedWaitlistByClassId] = useState<Record<string, WaitlistDoc>>({});
  const [busyClassId, setBusyClassId] = useState<string | null>(null);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);

  function pushNotice(message: string, tone: NoticeTone = "info") {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setNotices((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setNotices((current) => current.filter((notice) => notice.id !== id));
    }, 4200);
  }

  function dismissNotice(id: number) {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, [auth]);

  const windowLocal = useMemo(() => scheduleWindowWithSaturdayCutover(new Date()), []);

  useEffect(() => {
    const classesRef = collection(db, "classes");
    const q = query(
      classesRef,
      where("startTime", ">=", Timestamp.fromDate(windowLocal.from)),
      where("startTime", "<", Timestamp.fromDate(windowLocal.to)),
      orderBy("startTime", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          data: d.data() as ClassDoc,
        }));
        setClasses(rows);
      },
      (err) => console.error("classes onSnapshot error:", err)
    );

    return () => unsub();
  }, [windowLocal.from, windowLocal.to]);

  useEffect(() => {
    if (!user) {
      setActiveBookingsByClassId({});
      return;
    }

    const bookingsRef = collection(db, "bookings");
    const q = query(bookingsRef, where("userId", "==", user.uid), where("status", "==", "booked"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const map: Record<string, BookingDoc> = {};
        snap.docs.forEach((d) => {
          const booking = d.data() as BookingDoc;
          if (booking?.classId) map[booking.classId] = booking;
        });
        setActiveBookingsByClassId(map);
      },
      (err) => console.error("bookings onSnapshot error:", err)
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setActiveWaitlistByClassId({});
      return;
    }

    const waitlistsRef = collection(db, "waitlists");
    const q = query(waitlistsRef, where("userId", "==", user.uid), where("status", "==", "waiting"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const map: Record<string, WaitlistDoc> = {};
        snap.docs.forEach((d) => {
          const entry = d.data() as WaitlistDoc;
          if (entry?.classId) map[entry.classId] = entry;
        });
        setActiveWaitlistByClassId(map);
      },
      (err) => console.error("waitlists onSnapshot error:", err)
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPromotedWaitlistByClassId({});
      return;
    }

    const waitlistsRef = collection(db, "waitlists");
    const q = query(waitlistsRef, where("userId", "==", user.uid), where("status", "==", "promoted"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const map: Record<string, WaitlistDoc> = {};
        snap.docs.forEach((d) => {
          const entry = d.data() as WaitlistDoc;
          if (entry?.classId) map[entry.classId] = entry;
        });
        setPromotedWaitlistByClassId(map);
      },
      (err) => console.error("promoted waitlists onSnapshot error:", err)
    );

    return () => unsub();
  }, [user]);

  async function handleBook(classId: string) {
    if (!user) {
      pushNotice("Log in first.", "error");
      return;
    }

    setBusyClassId(classId);
    try {
      const result = await bookClassRequest({ classId });
      const outcome = (result.data as { outcome?: "booked" | "waitlisted" } | undefined)?.outcome;

      if (outcome === "waitlisted") {
        pushNotice("Class is full. You've been added to the waitlist.", "info");
      } else {
        pushNotice("Booking confirmed.", "success");
      }
    } catch (e: any) {
      console.error("Book failed:", e);
      const code = String(e?.code ?? "");
      const message = String(e?.message ?? "");
      if (code.includes("already-exists") || message.includes("Already booked")) {
        pushNotice("Already booked.", "info");
        return;
      }
      if (code.includes("already-exists") || message.includes("Already waitlisted")) {
        pushNotice("You're already on the waitlist.", "info");
        return;
      }
      if (code.includes("failed-precondition") && message.includes("Booking closed")) {
        pushNotice("Booking closed for this class.", "error");
        return;
      }
      pushNotice(e?.message || "Booking failed.", "error");
    } finally {
      setBusyClassId(null);
    }
  }

  async function handleCancel(classId: string) {
    if (!user) {
      pushNotice("Log in first.", "error");
      return;
    }

    setBusyClassId(classId);
    try {
      const result = await cancelBookingRequest({ classId });
      const promotedUserId = (result.data as { promotedUserId?: string } | undefined)?.promotedUserId;
      if (promotedUserId) {
        pushNotice("Booking cancelled. The next member on the waitlist was promoted automatically.", "info");
      } else {
        pushNotice("Booking cancelled.", "success");
      }
    } catch (e: any) {
      console.error("Cancel failed:", e);
      const code = String(e?.code ?? "");
      const message = String(e?.message ?? "");
      if (code.includes("not-found") || message.includes("No active booking found")) {
        pushNotice("No active booking found.", "error");
        return;
      }
      if (code.includes("failed-precondition") && message.includes("Cancellation closed")) {
        pushNotice("Too late to cancel — cancellations close 1 hour before class.", "error");
        return;
      }
      pushNotice(e?.message || "Cancel failed.", "error");
    } finally {
      setBusyClassId(null);
    }
  }

  async function handleLeaveWaitlist(classId: string) {
    if (!user) {
      pushNotice("Log in first.", "error");
      return;
    }

    setBusyClassId(classId);
    try {
      await leaveWaitlistRequest({ classId });
      pushNotice("You've left the waitlist.", "success");
    } catch (e: any) {
      console.error("Leave waitlist failed:", e);
      pushNotice(e?.message || "Could not leave waitlist.", "error");
    } finally {
      setBusyClassId(null);
    }
  }

  const grouped = useMemo(() => {
    const groups: Array<{
      key: string;
      dayLabel: string;
      shortLabel: string;
      items: Array<{ id: string; data: ClassDoc }>;
    }> = [];
    const map = new Map<string, number>();

    for (const classItem of classes) {
      const tz = classItem.data.timezone || DEFAULT_TZ;
      const start = classItem.data.startTime.toDate();
      const key = fmtDate(start, tz);

      if (!map.has(key)) {
        map.set(key, groups.length);
        groups.push({
          key,
          dayLabel: key,
          shortLabel: fmtDayLabel(start, tz),
          items: [],
        });
      }

      groups[map.get(key)!].items.push(classItem);
    }

    return groups;
  }, [classes]);

  const weekLabel = useMemo(() => {
    const a = windowLocal.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const endMinus1 = new Date(windowLocal.to.getTime() - 1);
    const b = endMinus1.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    return `${a} – ${b}`;
  }, [windowLocal.from, windowLocal.to]);

  const bookedCount = Object.keys(activeBookingsByClassId).length;
  const waitlistedCount = Object.keys(activeWaitlistByClassId).length;
  const selectedGroup = grouped.find((group) => group.key === selectedDayKey) ?? grouped[0] ?? null;

  useEffect(() => {
    if (!grouped.length) {
      setSelectedDayKey(null);
      return;
    }

    setSelectedDayKey((current) => {
      if (current && grouped.some((group) => group.key === current)) {
        return current;
      }
      return grouped[0].key;
    });
  }, [grouped]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-black text-white">
      <NoticeStack notices={notices} onDismiss={dismissNotice} />
      <UserTopNav />

      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-6 lg:px-8">
        <div className="space-y-5">
          {grouped.length === 0 ? (
            <div className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-6 text-white/70">
              No classes scheduled for this window.
            </div>
          ) : (
            <>
              <section className="rounded-[1.75rem] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.14),transparent_32%),linear-gradient(180deg,rgba(12,14,22,0.98),rgba(8,9,14,0.98))] p-4 sm:p-5">
                <div className="flex flex-col gap-3 border-b border-white/8 pb-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:#aabaff]">
                      {windowLocal.showNextWeek ? "Next week" : "This week"}
                    </div>
                    <h1 className="mt-2 text-2xl font-heading tracking-tight text-white sm:text-3xl">
                      Class Timetable
                    </h1>
                  </div>

                  <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em]">
                    <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-white/62">
                      {weekLabel}
                    </span>
                    {bookedCount > 0 ? (
                      <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                        {bookedCount} booked
                      </span>
                    ) : null}
                    {waitlistedCount > 0 ? (
                      <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-2 text-sky-200">
                        {waitlistedCount} waitlisted
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <div className="flex gap-2.5">
                    {grouped.map((group) => {
                      const isSelected = group.key === selectedGroup?.key;

                      return (
                        <button
                          key={`nav-${group.key}`}
                          type="button"
                          onClick={() => setSelectedDayKey(group.key)}
                          className={[
                            "min-w-[110px] rounded-full border px-4 py-3 text-left transition",
                            isSelected
                              ? "border-[color:rgba(136,156,255,0.24)] bg-[linear-gradient(180deg,rgba(61,88,177,0.34),rgba(33,48,99,0.28))] text-[color:#eef3ff] shadow-[0_8px_24px_rgba(41,67,155,0.24)]"
                              : "border-white/10 bg-white/[0.03] text-white/68 hover:border-[color:rgba(136,156,255,0.18)] hover:bg-[color:rgba(59,84,170,0.12)] hover:text-white/84",
                          ].join(" ")}
                        >
                          <div className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                            {group.shortLabel}
                          </div>
                          <div className="mt-1 text-sm font-semibold">
                            {group.items.length} class{group.items.length === 1 ? "" : "es"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {selectedGroup ? (
                <section
                  className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-5 sm:p-6"
                >
                  <div className="flex flex-col gap-2 border-b border-white/8 pb-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                        {selectedGroup.shortLabel}
                      </div>
                      <h2 className="mt-2 text-2xl font-heading tracking-tight text-white">
                        {selectedGroup.dayLabel}
                      </h2>
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/36">
                      {selectedGroup.items.length} session{selectedGroup.items.length === 1 ? "" : "s"}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3">
                    {selectedGroup.items.map(({ id, data }) => {
                      const tz = data.timezone || DEFAULT_TZ;
                      const start = data.startTime.toDate();
                      const end = data.endTime.toDate();
                      const capacity = Number(data.capacity ?? 0);
                      const bookedCountForClass = Number(data.bookedCount ?? 0);
                      const waitlistCount = Number(data.waitlistCount ?? 0);
                      const booked = Boolean(activeBookingsByClassId[id]);
                      const waitlisted = Boolean(activeWaitlistByClassId[id]);
                      const promoted = Boolean(promotedWaitlistByClassId[id] && booked);
                      const full = capacity > 0 && bookedCountForClass >= capacity;

                      const booking = bookingStatus(data.startTime);
                      const bookingClosed = booking.state === "closed" || booking.state === "started";
                      const cancel = cancelStatus(data.startTime);
                      const cancelClosed = cancel.state === "closed" || cancel.state === "started";
                      const meta = classMeta(data.title);
                      const percent = capacityPercent(bookedCountForClass, capacity);
                      const Icon = meta.Icon;

                      return (
                        <div
                          key={id}
                          className="relative overflow-hidden rounded-[1.35rem] border border-white/10 bg-[linear-gradient(180deg,rgba(17,21,34,0.84),rgba(9,11,18,0.72))] p-3.5 shadow-[0_10px_28px_rgba(0,0,0,0.28)] transition hover:border-[color:rgba(136,156,255,0.18)] hover:bg-[linear-gradient(180deg,rgba(19,24,39,0.9),rgba(10,12,20,0.76))] sm:p-4"
                        >
                          <div className={`absolute inset-y-0 left-0 w-1 ${meta.stripe}`} />

                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0 flex-1 pl-2">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[1rem] border ${meta.iconWrap}`}>
                                  <Icon className="h-4.5 w-4.5" />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div className="text-lg font-heading tracking-[0.1em] text-white">
                                      {meta.label}
                                    </div>
                                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${meta.pill}`}>
                                      {fmtTime(start, tz)}–{fmtTime(end, tz)}
                                    </span>
                                    {booked ? (
                                      <span className="inline-flex rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">
                                        Booked
                                      </span>
                                    ) : null}
                                    {waitlisted ? (
                                      <span className="inline-flex rounded-full border border-sky-500/20 bg-sky-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-200">
                                        Waitlisted
                                      </span>
                                    ) : null}
                                    {promoted ? (
                                      <span className="inline-flex rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">
                                        Promoted
                                      </span>
                                    ) : null}
                                    {full && !booked ? (
                                      <span className="inline-flex rounded-full border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-red-200">
                                        Full
                                      </span>
                                    ) : null}
                                  </div>

                                  <div className="mt-2.5 grid gap-2 text-[13px] text-white/60 sm:grid-cols-3">
                                    <div className="flex items-center gap-2">
                                      <Clock3 className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                      <span>{fmtTime(start, tz)} start</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <ShieldCheck className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                      <span>{data.coachName || "Coach TBC"}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-3.5 w-3.5 text-[color:#aabaff]" />
                                      <span>{data.location || "Main Floor"}</span>
                                    </div>
                                  </div>

                                  <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                                    <div>
                                      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/34">
                                        <span>Capacity</span>
                                        <span>
                                          {bookedCountForClass}/{capacity || "—"}
                                        </span>
                                      </div>
                                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white/8">
                                        <div
                                          className="h-full rounded-full bg-[linear-gradient(90deg,rgba(96,165,250,0.95),rgba(59,84,170,0.95))]"
                                          style={{ width: `${percent}%` }}
                                        />
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em]">
                                      {waitlistCount > 0 ? (
                                        <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-white/68">
                                          Waitlist {waitlistCount}
                                        </span>
                                      ) : null}
                                      {!booked && !full && booking.state === "open" && booking.msLeft != null ? (
                                        <span className="inline-flex rounded-full border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.18)] px-2.5 py-1 text-[color:#dbe6ff]">
                                          Closes in {fmtRemaining(booking.msLeft)}
                                        </span>
                                      ) : null}
                                      {!booked && bookingClosed ? (
                                        <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-white/44">
                                          Booking closed
                                        </span>
                                      ) : null}
                                      {booked && !cancelClosed && cancel.msLeft != null ? (
                                        <span className="inline-flex rounded-full border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.18)] px-2.5 py-1 text-[color:#dbe6ff]">
                                          Cancel closes in {fmtRemaining(cancel.msLeft)}
                                        </span>
                                      ) : null}
                                      {booked && cancelClosed ? (
                                        <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-white/44">
                                          Cancellation closed
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex w-full flex-col gap-2 lg:w-auto lg:min-w-[170px]">
                              {isAdmin ? (
                                <button
                                  onClick={() => navigate(`/admin/classes/${id}`)}
                                  className="inline-flex items-center justify-center gap-2 rounded-[1rem] border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.16)] px-4 py-2.5 text-sm font-semibold text-[color:#dbe6ff] transition hover:bg-[color:rgba(59,84,170,0.24)]"
                                >
                                  Roster
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              ) : null}

                              {booked ? (
                                <button
                                  onClick={() => handleCancel(id)}
                                  disabled={busyClassId === id || cancelClosed}
                                  className={[
                                    "rounded-[1rem] border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40",
                                    cancelClosed
                                      ? "border-white/10 bg-white/[0.04] text-white/34"
                                      : "border-white/10 bg-white/[0.03] text-white/82 hover:bg-white/[0.06]",
                                  ].join(" ")}
                                >
                                  {cancelClosed ? "Too late" : busyClassId === id ? "Cancelling…" : "Cancel booking"}
                                </button>
                              ) : waitlisted ? (
                                <button
                                  onClick={() => handleLeaveWaitlist(id)}
                                  disabled={busyClassId === id || bookingClosed}
                                  className={[
                                    "rounded-[1rem] border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40",
                                    bookingClosed
                                      ? "border-white/10 bg-white/[0.04] text-white/34"
                                      : "border-sky-500/20 bg-sky-500/10 text-sky-100 hover:bg-sky-500/15",
                                  ].join(" ")}
                                >
                                  {bookingClosed ? "Closed" : busyClassId === id ? "Leaving…" : "Leave waitlist"}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBook(id)}
                                  disabled={busyClassId === id || bookingClosed}
                                  className={[
                                    "rounded-[1rem] border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40",
                                    bookingClosed
                                      ? "border-white/10 bg-white/[0.04] text-white/34"
                                      : "border-emerald-500/20 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15",
                                  ].join(" ")}
                                >
                                  {bookingClosed ? "Closed" : busyClassId === id ? "Working…" : full ? "Join waitlist" : "Book session"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>

      <LogoutButton />
    </div>
  );
}
